import styled, { css } from 'styled-components';
import { Col, Form, FormItemProps as AntFormItemProps, Row, Switch } from 'antd';
import { useMemo, useState, VFC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { isNil } from 'rambda';
import { FormItem as messages } from './messages';
import { getSuggestionConfig } from './helpers';
import { useMeterModelSuggestionsStore } from '../../store/useMeterModelSuggestionsStore';
import { CombinedMeterModelDetails } from '../../hooks/meter_models/types';
import { theme } from '../../consts/theme';

// Units for detail fields
const TYPE_DETAILS_UNITS: Record<keyof Pick<CombinedMeterModelDetails, 'flowrate' | 'width'>, string> = {
  width: 'mm',
  flowrate: 'Q3',
};

const StyledFormItem = styled(Form.Item)`
  ${({ noStyle = false }) =>
    noStyle
      ? ''
      : css`
          margin-bottom: 14px;

          .ant-form-item-label {
            padding-bottom: 2px;
          }

          .ant-form-item-explain {
            font-size: 12px;
          }

          .ant-form-item-extra {
            font-size: 12px;
            color: ${theme.colors.dustRed};
          }
        `}
`;

export interface FormItemProps extends AntFormItemProps {
  noStyle?: boolean;
  isSemiRequired?: boolean;
  onResetField?: (name: AntFormItemProps['name']) => void;
}

export const FormItem: VFC<FormItemProps> = props => {
  const { children, isSemiRequired, name, onResetField, rules, ...rest } = props;
  const intl = useIntl();
  const form = Form.useFormInstance();
  const { meterModelSuggestions } = useMeterModelSuggestionsStore();

  const [isValueDefined, setValueDefined] = useState(() => {
    // Derive initial switch state from the field's value.
    // If the field was reset (set to null via onResetField), keep the switch off on remount.
    if (isSemiRequired && rest.initialValue === null) {
      return false;
    }
    return true;
  });

  const { fieldKey, suggestedValue } = getSuggestionConfig(name, meterModelSuggestions);

  const hasValidSuggestedValue = !isNil(suggestedValue);

  // Reset form value to suggested value only on first mount
  useEffect(() => {
    if (name && fieldKey && hasValidSuggestedValue) {
      if (fieldKey === 'width') {
        form.setFieldValue(['detail', fieldKey], {
          value: suggestedValue,
          unit: TYPE_DETAILS_UNITS[fieldKey],
        });
        return;
      }

      form.setFieldValue(name, suggestedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this runs only once on mount

  const validationRules = useMemo(
    () => (rules?.length ? rules : [{ required: true, message: intl.formatMessage(messages.requiredField) }]),
    [rules, intl],
  );

  const handleReset = (state: boolean) => {
    setValueDefined(state);
    if (!state) onResetField?.(name);
  };

  // Render semi-required form item with switch
  if (isSemiRequired) {
    return (
      <StyledFormItem required={isValueDefined} {...rest}>
        <Row align="middle" gutter={8}>
          <Col>
            <Switch checked={isValueDefined} onChange={handleReset} />
          </Col>

          <Col flex="auto">
            <StyledFormItem
              {...rest}
              noStyle
              hidden={!isValueDefined}
              name={name}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              normalize={value => (value === undefined ? null : value)}
              required={isValueDefined}
              rules={isValueDefined ? validationRules : []}
            >
              {children}
            </StyledFormItem>
          </Col>
        </Row>
      </StyledFormItem>
    );
  }

  // Default case - render regular form item
  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    <StyledFormItem name={name} normalize={value => (value === undefined ? null : value)} rules={rules} {...rest}>
      {children}
    </StyledFormItem>
  );
};
